import React from 'react'
import PropTypes from 'prop-types'
import { Global, css } from '@emotion/react'
import styled from '@emotion/styled'
import Link from 'next/link'
import cookies from 'js-cookie'
import { withTranslation } from '~/i18n'
import useLogRecentlyVisitedEvents from '~/hooks/useLogRecentlyVisitedEvents'
import { GET_VIEWER } from '~/lib/get-viewer'
import debounce from 'lodash/debounce'
import { Query } from '@apollo/client/react/components'
import Downshift, { resetIdCounter } from 'downshift'
import {
  Input,
  H6,
  device,
  space,
  transition,
  Button,
  color,
  HorizontalScroll,
} from '@ticketswap/solar'
import { ChevronLeftAlt, MagnifyingGlass } from '@ticketswap/comets'
import { GET_SEARCH_RESULTS } from './getSearchResults'
import { GET_TRENDING_EVENTS } from './getTrendingEvents'
import { Menu, MenuBody, MenuFooter, ShowMoreLink, Item, NothingFound } from './Menu'
import { SEARCH_COMPLETED, SEARCH_STARTED, SEARCH_RESULT_CLICKED } from '../../lib/tracking-events'
import { track, getLinkProps } from '../../utils'
import getGeoInfo from '../../utils/getGeoInfo'
import { decodeGlobalId } from '~/utils/id'
import visit from '~/utils/visit'

const Container = styled.div`
  position: ${props => (props.isOpen ? 'fixed' : 'relative')};
  margin: 0 auto;
  overflow-y: scroll;
  -webkit-overflow-scrolling: touch;

  ${props =>
    props.isOpen &&
    css`
      left: 0;
      top: 0;
      right: 0;
      bottom: 0;
      background-color: ${color.nova};
      z-index: 4;
    `};

  @media ${device.tablet} {
    overflow-y: visible;
    max-width: ${props => (props.width === 'medium' ? `${640 / 16}rem` : 'none')};
    position: relative;
    left: auto;
    top: auto;
    right: auto;
    bottom: auto;
    background-color: transparent;
  }
`

const InputWrapper = styled.div`
  position: relative;
  padding-top: ${props => (props.isOpen ? space[16] : 0)};
  padding-bottom: ${props => (props.isOpen ? space[16] : 0)};
  padding-right: ${props => (props.isOpen ? space[16] : 0)};
  padding-left: ${props => (props.isOpen ? space[56] : 0)};
  display: flex;
  flex-direction: row;
  align-items: center;
  align-content: stretch;

  > * {
    flex-basis: 100%;
  }

  @media ${device.tablet} {
    padding: 0;
  }
`

const ToggleButton = styled.button`
  position: absolute;
  left: 0;
  color: ${color.spaceMedium};
  padding: ${space[12]};
  outline: 0;
  transition: color ${transition};

  @media ${device.tablet} {
    display: none;
  }

  &:hover,
  &:focus {
    color: ${color.space};
  }
`

const FilterWrapper = styled.div`
  margin-bottom: ${space[16]};

  @media ${device.tablet} {
    display: none;
  }
`

const StyledH6 = styled(H6)`
  padding-top: ${space[4]};

  @media ${device.tablet} {
    padding-top: ${space[8]};
  }
`

function SiteSearch({ t, ...props }) {
  const noScrollClass = 'no-scroll'
  const inputRef = React.useRef()
  const [query, setQuery] = React.useState('')
  const [filter, setFilter] = React.useState(null)
  const [recentlyVisitedEvents] = useLogRecentlyVisitedEvents()

  if (!process.browser) {
    resetIdCounter()
  }

  React.useEffect(() => {
    if (props.defaultOpen) {
      inputRef.current.focus()
    }
  }, [props])

  const handleSearch = debounce(query => {
    if (typeof query !== 'undefined') {
      setQuery(query)
    }
  }, 500)

  const trackSearchResultsDebounced = debounce((hasResults, query) => {
    track(SEARCH_COMPLETED, {
      hasResults,
      query,
    })
  }, 1000)

  const stateReducer = (state, changes) => {
    // Prevent menu from closing when hiding the
    // keyboard in mobile Safari.
    if (changes.type === Downshift.stateChangeTypes.blurInput) {
      return {} // no changes
    }
    // Close menu on desktop when input loses focus,
    // but keep `inputValue` as is.
    if (changes.type === Downshift.stateChangeTypes.mouseUp) {
      return {
        ...changes,
        isOpen: false,
        inputValue: state.inputValue,
      }
    }
    // Close menu when clicking toggle button and
    // set `inputValue` to ''.
    if (changes.type === Downshift.stateChangeTypes.clickButton) {
      return {
        ...changes,
        isOpen: false,
        inputValue: '',
      }
    }
    return changes
  }

  const handleStateChange = ({ isOpen, inputValue }) => {
    // Perform search
    handleSearch(inputValue)
    // Toggle `.no-scroll` class on body
    if (typeof isOpen !== 'undefined') {
      let timer = null
      if (isOpen) {
        track(SEARCH_STARTED)
        if (!document.body.classList.contains(noScrollClass)) {
          document.body.classList.add(noScrollClass)
        }
        timer = setTimeout(() => {
          // Fixes an issue in mobile Safari where the input field
          // would be outside the visible viewport because of the
          // appearing keyboard. `60ms` may seem arbitrary, but felt
          // like the sweet spot when testing.
          if (!window.matchMedia(device.tablet).matches) {
            window.scrollTo(0, 0)
          }
        }, 60)
      } else {
        if (document.body.classList.contains(noScrollClass)) {
          document.body.classList.remove(noScrollClass)
        }
        clearTimeout(timer)
        // This is used to close the SearchOverlay wrapper
        props.onClose()
      }
    }
  }

  return (
    <Downshift
      id="site-search"
      onChange={selection => {
        if (!selection) return false
        const type = selection.__typename
        let handle = 'EVENT_PAGE'
        let params = {
          id: decodeGlobalId(selection.id),
          slug: selection.slug,
        }
        switch (type) {
          case 'CityResult':
            handle = 'CITY'
            break
          case 'LocationResult':
            handle = 'LOCATION'
            break
          case 'ArtistResult':
            handle = 'ARTIST'
            break
          default:
            params = {
              eventId: decodeGlobalId(selection.id),
              eventSlug: selection.slug,
            }
            break
        }

        const { href, as } = getLinkProps(handle, params)

        track(SEARCH_RESULT_CLICKED, {
          contentId: atob(selection.id).split(':')[1],
          contentName: selection.name,
          contentType: handle.split('_')[0],
          searchFilter: filter,
          link: as,
        })

        visit({ href, as })
      }}
      defaultOpen={props.defaultOpen}
      stateReducer={stateReducer}
      onStateChange={handleStateChange}
      itemToString={item => (item ? item.name : '')}
    >
      {({
        clearSelection,
        getInputProps,
        getItemProps,
        getLabelProps,
        getMenuProps,
        getToggleButtonProps,
        highlightedIndex,
        inputValue,
        isOpen,
        openMenu,
        selectedItem,
      }) => {
        return (
          <div>
            <Container isOpen={isOpen} width={props.width}>
              <Query query={GET_VIEWER} skip={!isOpen || !cookies.get('token')}>
                {({ data = {} }) => {
                  const user = data.viewer
                  const withRecentlyVisited = user && inputValue.length === 0 ? true : false

                  return (
                    <>
                      <label htmlFor="a11y-input" aria-hidden="true" style={{ display: 'none' }}>
                        <input type="text" id="a11y-input" tabIndex="-1" />
                      </label>
                      <Query
                        query={GET_TRENDING_EVENTS}
                        skip={!isOpen || withRecentlyVisited}
                        variables={{
                          ...getGeoInfo(),
                        }}
                      >
                        {({ loading: loadingEvents, data = {} }) => {
                          const { nearby } = data

                          return (
                            <Query
                              query={GET_SEARCH_RESULTS}
                              skip={!isOpen || query.length === 0}
                              variables={{
                                query,
                                first: props.resultsPerPage || 5,
                                type: filter,
                              }}
                              onCompleted={data => {
                                if (query) {
                                  trackSearchResultsDebounced(data?.search?.edges.length > 0, query)
                                }
                              }}
                            >
                              {({ loading: loadingSearchResults, data = {} }) => {
                                const { search } = data
                                const results = (search && search.edges) || []
                                const trendingEvents = (nearby && nearby.trendingEvents) || []
                                const hasNextPage =
                                  search && search.pageInfo && search.pageInfo.hasNextPage
                                const showSearchResults =
                                  (!loadingSearchResults && results && query.length > 0) ||
                                  (loadingSearchResults && results.length > 0)
                                const showTrending =
                                  !user &&
                                  !showSearchResults &&
                                  trendingEvents &&
                                  trendingEvents.length > 0
                                const showRecentlyVisited =
                                  user &&
                                  !showSearchResults &&
                                  recentlyVisitedEvents &&
                                  recentlyVisitedEvents.length > 0
                                const items = [
                                  ...(showSearchResults ? results : []),
                                  ...(showTrending ? trendingEvents : []),
                                  ...(showRecentlyVisited ? recentlyVisitedEvents : []),
                                ]
                                const encodedInputValue = encodeURIComponent(inputValue)
                                const showMenu =
                                  isOpen &&
                                  (showSearchResults || showRecentlyVisited || showTrending)
                                const heading = showSearchResults
                                  ? 'search.results'
                                  : showRecentlyVisited
                                  ? 'search.recentlyVisited'
                                  : 'search.trending'

                                return (
                                  <>
                                    <InputWrapper isOpen={isOpen}>
                                      {isOpen && (
                                        <ToggleButton {...getToggleButtonProps()}>
                                          <ChevronLeftAlt />
                                        </ToggleButton>
                                      )}
                                      <Input
                                        {...getInputProps({
                                          loading: loadingEvents || loadingSearchResults,
                                          ref: inputRef,
                                          label: t('search.label'),
                                          labelProps: getLabelProps(),
                                          type: 'search',
                                          leftAdornment: <MagnifyingGlass size={24} />,
                                          hideLabel: true,
                                          placeholder: t('search.info'),
                                          onFocus: openMenu,
                                          onReset: clearSelection,
                                        })}
                                      />
                                    </InputWrapper>
                                    {showMenu && (
                                      <Menu {...getMenuProps()}>
                                        {showSearchResults && (
                                          <FilterWrapper>
                                            <HorizontalScroll>
                                              <Button
                                                size="small"
                                                variant="secondary"
                                                rounded
                                                active={!filter}
                                                onClick={() => setFilter(null)}
                                              >
                                                {t('search.allResults')}
                                              </Button>
                                              <Button
                                                size="small"
                                                variant="secondary"
                                                rounded
                                                active={filter === 'EVENT'}
                                                onClick={() => setFilter('EVENT')}
                                              >
                                                {t('search.event_plural')}
                                              </Button>
                                              <Button
                                                size="small"
                                                variant="secondary"
                                                rounded
                                                active={filter === 'LOCATION'}
                                                onClick={() => setFilter('LOCATION')}
                                              >
                                                {t('search.venue_plural')}
                                              </Button>
                                              <Button
                                                size="small"
                                                variant="secondary"
                                                rounded
                                                active={filter === 'CITY'}
                                                onClick={() => setFilter('CITY')}
                                              >
                                                {t('search.city_plural')}
                                              </Button>
                                              <Button
                                                size="small"
                                                variant="secondary"
                                                rounded
                                                active={filter === 'ARTIST'}
                                                onClick={() => setFilter('ARTIST')}
                                              >
                                                {t('search.artist_plural')}
                                              </Button>
                                            </HorizontalScroll>
                                          </FilterWrapper>
                                        )}

                                        <MenuBody isOpen={isOpen}>
                                          {!(showSearchResults && results.length === 0) && (
                                            <StyledH6>{t(heading)}</StyledH6>
                                          )}

                                          {showSearchResults && items.length === 0 ? (
                                            <NothingFound
                                              t={t}
                                              query={query}
                                              closeSearch={props.onClose}
                                            />
                                          ) : null}

                                          {items.length > 0 &&
                                            items.map((el, index) => {
                                              const node = el.node ? el.node : el

                                              return (
                                                <Item
                                                  {...getItemProps({
                                                    key: node.id,
                                                    index,
                                                    item: node,
                                                    node,
                                                    t,
                                                    highlighted:
                                                      highlightedIndex === index
                                                        ? 'true'
                                                        : undefined,
                                                    selected:
                                                      selectedItem === index ? 'true' : undefined,
                                                  })}
                                                />
                                              )
                                            })}
                                        </MenuBody>

                                        {showSearchResults && hasNextPage ? (
                                          <MenuFooter>
                                            <Link
                                              {...getLinkProps('SEARCH', {
                                                query: encodedInputValue,
                                              })}
                                              passHref
                                            >
                                              <ShowMoreLink>{t('search.showMore')}</ShowMoreLink>
                                            </Link>
                                          </MenuFooter>
                                        ) : null}
                                      </Menu>
                                    )}
                                  </>
                                )
                              }}
                            </Query>
                          )
                        }}
                      </Query>
                    </>
                  )
                }}
              </Query>
            </Container>
            <Global
              styles={css`
                body.no-scroll {
                  position: fixed;
                  left: 0;
                  top: 0;
                  right: 0;
                  bottom: 0;

                  @media ${device.tablet} {
                    position: static;
                    left: auto;
                    top: auto;
                    right: auto;
                    bottom: auto;
                  }
                }
              `}
            />
          </div>
        )
      }}
    </Downshift>
  )
}

SiteSearch.propTypes = {
  defaultOpen: PropTypes.bool,
  onClose: PropTypes.func,
}

SiteSearch.defaultProps = {
  defaultOpen: false,
  onClose: () => {},
}

export default withTranslation('common')(SiteSearch)
